import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import AuthenticationService from "service/auth/AuthenticationService";
import usePopover from "components/common/usePopover";
import { accountDetails } from "components/common/UserMenu";

/**
 * The signed-in user's avatar, and the panel it opens.
 *
 * Desktop's UserMenu is a name chip whose menu holds the account's DOCTOR CODE
 * (a chip with a copy button) and PHONE above a divider, then Log out. This is
 * that panel at 360px, and it shows the SAME two details, from the same
 * `accountDetails` helper — an account menu that disagreed with desktop about
 * what the account is would be worse than either.
 *
 * THE NAME STAYS, and is the one thing desktop's menu does not draw. Desktop
 * does not need to: its trigger is a chip with the name written on it. Here
 * the trigger is the avatar alone, because the name does not fit beside the
 * title — so the panel is the only place it appears, and without it there is
 * no way to see WHICH account you are about to log out of.
 *
 * READ-ONLY, AND READ FROM THE STORED PROFILE. Nothing here fetches: every
 * value is already in the profile the login wrote (AuthenticationService's
 * KEY_USER_PROFILE), which is the same place the desktop chip takes its name
 * and image from. A detail that is not on the profile is not drawn — an
 * employee login carries no doctorProfile, so it has no doctor code to print.
 *
 * NO ROLE SUBTITLE, for the reason UserMenu.js records: the string used to be
 * hardcoded "Care Manager" for everyone, which told doctors they were care
 * managers. A real one costs a request per session to print a job title beside
 * someone's own name.
 */

/** Figma's icon/log-out, 17x15 (3015:8489) — the same path desktop uses. */
const LogOutIcon = () => (
  <svg
    viewBox="0 0 17 15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M9.5 1H3C1.9 1 1 1.9 1 3V12C1 13.1 1.9 14 3 14H9.5" />
    <path d="M7.5 7.5H15.5M12.7 11L15.5 7.5L12.7 4" />
  </svg>
);

/** Material "content_copy" / "check", at the board's 14px — desktop's glyphs. */
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

/* Copies text, with the textarea fallback for a context without the async API.
   UserMenu.js has the same helper and PaymentLinkDialog a third — kept local
   rather than reaching into a desktop component for it, so mobile stays
   readable on its own. It is clipboard mechanics, not the account contract:
   what the panel SAYS comes from desktop's accountDetails, and that is the
   part that must not drift. */
const copyText = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) resolve();
    else reject(new Error("copy failed"));
  });
};

const MENU_WIDTH = 248;

/**
 * Who is signed in, as stored. Exported so the patient screen's overflow menu
 * (MobileTopBar) shows the same block — two account menus that disagree about
 * what an account is would be worse than either.
 */
export const AccountIdentity = () => {
  /* One instance, reused: constructing AuthenticationService re-runs
     setDefaultCookies(), which rewrites the `client` cookie on every render. */
  const [auth] = useState(() => new AuthenticationService());

  /* DESKTOP'S OWN READ. `accountDetails` picks the doctor code and walks
     doctor -> employee -> account for the phone, then groups it 5 + 5 behind
     its country code. It used to be read here instead — `doctorProfile.id`
     (the internal row id, "845") and a raw `phoneno` — which showed a
     different identity for the same account than the desktop menu did.

     READ ONCE, as the desktop chip is (UserMenu): logout() empties storage
     before its navigation lands, and a re-render in between showed "User". */
  const [{ name, code, phone }] = useState(() => ({
    name: auth.getUserName(),
    ...accountDetails(auth.getUserProfile()),
  }));

  /* "Copied" for a moment after the button is pressed — the glyph turns into a
     check and the button says so to assistive tech. */
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);
  const copyCode = () => {
    copyText(code).then(
      () => {
        setCopied(true);
        clearTimeout(copiedTimer.current);
        copiedTimer.current = setTimeout(() => setCopied(false), 1500);
      },
      () => {}
    );
  };

  return (
    <div className="zym-acct">
      <p className="zym-acct-name">{name}</p>

      {code ? (
        <div className="zym-acct-field">
          <span className="zym-acct-label" id="zym-acct-code-label">Doctor code</span>
          <span className="zym-acct-valrow">
            <span className="zym-acct-chip" aria-labelledby="zym-acct-code-label">{code}</span>
            <button
              type="button"
              className={`zym-acct-copy${copied ? " is-copied" : ""}`}
              aria-label={copied ? "Doctor code copied" : "Copy doctor code"}
              title={copied ? "Copied" : "Copy doctor code"}
              onClick={copyCode}
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </button>
          </span>
        </div>
      ) : null}

      {phone ? (
        <div className="zym-acct-field is-tight">
          <span className="zym-acct-label">Phone</span>
          <span className="zym-acct-value">{phone}</span>
        </div>
      ) : null}
    </div>
  );
};

const MobileAccountMenu = () => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, () => setOpen(false));
  const [auth] = useState(() => new AuthenticationService());

  /* Read once, as the identity block is: re-read after logout() had emptied
     storage, the avatar blanked for a moment before the navigation landed. */
  const [image] = useState(() => auth.getUserProfileImage());

  /* auth.logout() ends in a hard window.location.href = "/", and that reload is
     load-bearing — two module-level singletons only get torn down by a real
     navigation. UserMenu.js documents this at length; do not turn it into a
     router push here either. */
  const logout = () => {
    setOpen(false);
    auth.logout();
  };

  /* usePopover left-aligns to the trigger and has no right-align mode, and this
     trigger is hard against the right edge — so a menu wider than the button
     would be dragged back by the viewport clamp. Right-align it by hand, the
     same correction UserMenu.js and MobileTopBar make. */
  const style =
    menuStyle.left != null && triggerRef.current
      ? {
          top: menuStyle.top,
          left: Math.max(8, triggerRef.current.getBoundingClientRect().right - MENU_WIDTH),
        }
      : { top: menuStyle.top, left: menuStyle.left };

  return (
    <div className="zym-acct-wrap">
      <button
        type="button"
        ref={triggerRef}
        className="zym-acct-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        onClick={() => setOpen((o) => !o)}
      >
        {image ? <img src={image} alt="" /> : null}
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="zym-menu zym-acct-menu"
            style={style}
            role="menu"
            aria-label="Account"
          >
            <AccountIdentity />
            <button
              type="button"
              className="zym-menu-item is-danger"
              role="menuitem"
              onClick={logout}
            >
              <LogOutIcon />
              Log out
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default MobileAccountMenu;
